import React from "react";
import { getEmptyDataDefinition, getFilledDataDefinition } from "../../rule-engine-core/Common/Data/DataTestData";
import VariablePicker, { IVariablePicker } from "./VariablePicker";
import { DataTypes } from "../../rule-engine-core/Common/Data/DataTypes";
import { DataDefinition } from "../../rule-engine-core/Common/Data/DataDefinition";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { ObserveState } from "../StateManagement/ObserveState";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { ObjectDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { StringDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { StoryNode } from "../StoryBook/StoryData";

class VariablePickerParams implements IVariablePicker {
    data: DataDefinition = getFilledDataDefinition();
    selected: string | null | undefined = null;
    requiredType: DataTypes | ObjectDataDefinition | undefined = DataTypes.STRING;
    allowNewVariable?: boolean = undefined;
    variant?: "outlined" | "standard" | "filled";
    size?: "small" | "medium" | undefined;
    fullWidth?: boolean;
    startOpenIfEmptyOrInvalid?: boolean;
    isList?: boolean | undefined;

    constructor(data?: DataDefinition) {
        if (data) this.data = data;
    }

    onVariableSet = (path: string, def?: IDataDefinition) => {
        let changedProperties = ["selected"];
        if (def) {
            let data: DataDefinition | ObjectDataDefinition = this.data;
            if (path !== "") {
                data = DataUtilities.getFieldFromDataDefinitionByName(this.data.fields, path) as ObjectDataDefinition;

                this.selected = `${path}.${def.name}`;
            } else {
                this.selected = def.name;
            }
            data.fields.push(def);
            changedProperties.push("data");
        } else {
            this.selected = path;
        }
        StateManager.propertiesChanged(this, changedProperties);
    };
}

export const VariablePickerStories = [
    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams(getEmptyDataDefinition());
            variablePickerProps.allowNewVariable = true;

            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Empty With Editable",
    ),

    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams(getEmptyDataDefinition());
            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Empty",
    ),
    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams();

            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Not Editable",
    ),
    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams();
            variablePickerProps.allowNewVariable = true;

            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Editable",
    ),
    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams();
            variablePickerProps.requiredType = DataTypes.BOOLEAN;
            variablePickerProps.selected = "person.firstName";

            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Invalid Type",
    ),
    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams();
            variablePickerProps.requiredType = variablePickerProps.data.fields[
                variablePickerProps.data.fields.length - 1
            ] as ObjectDataDefinition;
            variablePickerProps.selected = "person";
            variablePickerProps.allowNewVariable = true;

            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Object Definition Required Type",
    ),
    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams();
            let children = new ObjectDataDefinition();
            children.name = "children";
            children.list = true;

            let childFirstName = new StringDataDefinition();
            childFirstName.name = "firstName";
            children.fields.push(childFirstName);

            let childLastName = new StringDataDefinition();
            childLastName.name = "lastName";
            children.fields.push(childLastName);

            variablePickerProps.data.fields.push(children);
            variablePickerProps.requiredType = children;
            variablePickerProps.selected = "person";
            variablePickerProps.allowNewVariable = true;
            variablePickerProps.requiredType.list = true;

            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Object Definition With List Required Type",
    ),
    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams();
            variablePickerProps.requiredType = undefined;
            variablePickerProps.allowNewVariable = true;

            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Without Required Type",
    ),
    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams();
            variablePickerProps.size = "medium";
            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Medium Size",
    ),
    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams();
            variablePickerProps.size = "small";
            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Small Size",
    ),

    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams();
            variablePickerProps.selected = "invalid";
            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Invalid Field",
    ),
    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams();
            variablePickerProps.selected = "invalid";
            variablePickerProps.startOpenIfEmptyOrInvalid = true;

            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Start Open If Empty or Invalid",
    ),
    new StoryNode(
        () => {
            let variablePickerProps = new VariablePickerParams();
            variablePickerProps.requiredType = undefined;

            variablePickerProps.isList = true;
            return (
                <ObserveState
                    listenTo={variablePickerProps}
                    control={() => (
                        <div style={{ display: "flex", flexDirection: "column", maxWidth: 250 }}>
                            <VariablePicker {...variablePickerProps} />
                        </div>
                    )}
                />
            );
        },
        "Shared",
        "Variable Picker",
        "Only List",
    ),
];
